import { apiClient } from './client';

// docs/plan/team-dashboards-and-production-handoff/plan.md §7

export interface ProductionPlan {
  id: string;
  humanId: string;
  batchCode: string | null;
  modelId: string;
  model?: { id: string; code: string; displayName: string };
  plannedCount: number;
  estimatedCompletionAt: string;
  actualCompletionAt: string | null;
  progressPercent: number;
  status: 'PLANNED' | 'IN_PROGRESS' | 'COMPLETED' | 'PARTIALLY_COMPLETED' | 'DELAYED' | 'CANCELLED';
  ownerEntity: string;
  notes: string | null;
  priority: number;
  // S2 Phase 2.9 (§9.4) — full 세분화 spec 3축 (model × 색상 × 불감지 × 구분사양).
  color: string | null;
  sensorType: string | null;
  variantValue: string | null;
  // data-lifecycle §3 — soft-delete 스탬프(삭제됨 배지 + 복구용).
  deletedAt?: string | null;
  // computed (list response 만)
  allocatedCount?: number;
  materializedCount?: number;
  availableCount?: number;
  allocations?: Array<{
    id: string;
    allocatedCount: number;
    dealId: string | null;
    contractId: string | null;
    allocatedAt: string;
    deal?: { id: string; humanId: string } | null;
    contract?: { id: string; humanId: string } | null;
  }>;
  createdAt: string;
  updatedAt: string;
}

export interface ListProductionPlansResponse {
  items: ProductionPlan[];
  total: number;
  page: number;
  pageSize: number;
}

// FB2 §7B (결정 E) — plan → 실제 Robot row 머티리얼라이즈 요청 body.
// 백엔드 `MaterializeProductionPlanDto`(apps/api/.../dto/materialize.dto.ts)와 1:1.
// robots 배열 길이 = 생성할 로봇 수. 각 spec 필드는 모두 선택(서버가 plan
// 의 color/sensorType/variantValue 를 자동 상속; per-robot color 만 override).
// SN(serialShort/serialLong)은 운영자 입력 — 빈 값이면 null(@unique 는 다중 null 허용).
export interface MaterializeRobotSpec {
  serialShort?: string;
  serialLong?: string;
  accountingBookNo?: string;
  color?: string;
  loaderType?: string;
  firstProductionYm?: string;
  manufactureCompletedAt?: string; // ISO
  notes?: string;
}

export interface MaterializeProductionPlanBody {
  robots: MaterializeRobotSpec[];
  // 생성과 동시에 contract reservation 까지 처리할지 (default true).
  autoReserveContract?: boolean;
}

export interface MaterializeProductionPlanResponse {
  planId: string;
  status: ProductionPlan['status'];
  materializedCount: number;
  robotIds: string[];
  reservedContractIds: string[];
}

export interface ListProductionPlansFilters {
  modelId?: string;
  status?: string;
  // S2 Phase 2.9 (§9.4) — spec 3축 exact-match 필터.
  color?: string;
  sensorType?: string;
  variantValue?: string;
  fromDate?: string;
  toDate?: string;
  unallocatedOnly?: boolean;
  // data-lifecycle §3 — 삭제됨(soft-delete) 포함(제조팀 전용; 서버에서 역할 확인).
  includeDeleted?: boolean;
  page?: number;
  pageSize?: number;
}

export const productionPlansApi = {
  async list(filters: ListProductionPlansFilters = {}): Promise<ListProductionPlansResponse> {
    const params = new URLSearchParams();
    if (filters.modelId) params.set('modelId', filters.modelId);
    if (filters.status) params.set('status', filters.status);
    if (filters.color) params.set('color', filters.color);
    if (filters.sensorType) params.set('sensorType', filters.sensorType);
    if (filters.variantValue) params.set('variantValue', filters.variantValue);
    if (filters.fromDate) params.set('fromDate', filters.fromDate);
    if (filters.toDate) params.set('toDate', filters.toDate);
    if (filters.unallocatedOnly) params.set('unallocatedOnly', 'true');
    if (filters.includeDeleted) params.set('includeDeleted', 'true');
    if (filters.page) params.set('page', String(filters.page));
    if (filters.pageSize) params.set('pageSize', String(filters.pageSize));
    const { data } = await apiClient.get<ListProductionPlansResponse>(
      `/production-plans?${params.toString()}`,
    );
    return data;
  },

  async get(id: string): Promise<ProductionPlan> {
    const { data } = await apiClient.get<ProductionPlan>(`/production-plans/${id}`);
    return data;
  },

  async create(payload: {
    modelId: string;
    plannedCount: number;
    estimatedCompletionAt: string;
    batchCode?: string;
    notes?: string;
    priority?: number;
    ownerEntity?: string;
    // S2 Phase 2.9 (§9.4) — full 세분화 spec 3축.
    color?: string;
    sensorType?: string;
    variantValue?: string;
  }): Promise<ProductionPlan> {
    const { data } = await apiClient.post<ProductionPlan>('/production-plans', payload);
    return data;
  },

  async update(id: string, payload: Partial<ProductionPlan>): Promise<ProductionPlan> {
    const { data } = await apiClient.patch<ProductionPlan>(`/production-plans/${id}`, payload);
    return data;
  },

  async remove(id: string): Promise<void> {
    await apiClient.delete(`/production-plans/${id}`);
  },

  // data-lifecycle plan §3 — soft-delete 복구(셀프, 제조 팀).
  async restore(id: string): Promise<void> {
    await apiClient.post(`/production-plans/${encodeURIComponent(id)}/restore`);
  },

  // data-lifecycle plan §3/D3 — 셀프 완전삭제(소프트삭제된 계획만, FK 가드).
  async hardDelete(id: string): Promise<void> {
    await apiClient.delete(`/production-plans/${encodeURIComponent(id)}/permanent`);
  },

  async allocate(
    planId: string,
    payload: {
      allocatedCount: number;
      dealId?: string;
      contractId?: string;
      notes?: string;
    },
  ) {
    const { data } = await apiClient.post(`/production-plans/${planId}/allocate`, payload);
    return data;
  },

  async release(allocationId: string, releaseReason?: string) {
    const { data } = await apiClient.post('/production-plans/allocations/release', {
      allocationId,
      releaseReason,
    });
    return data;
  },

  // FB2 §7B (결정 E) — 운영자가 "완성 처리" 버튼으로 plan 의 일부/전부를 실제
  // Robot row(status READY_TO_SHIP)로 생성. POST /production-plans/:id/materialize.
  async materialize(
    planId: string,
    body: MaterializeProductionPlanBody,
  ): Promise<MaterializeProductionPlanResponse> {
    const { data } = await apiClient.post<MaterializeProductionPlanResponse>(
      `/production-plans/${planId}/materialize`,
      body,
    );
    return data;
  },
};
